import React, { useState } from 'react';
import Terminal from './components/Terminal';
import CustomTerminal from './components/CustomTerminal';
import AuthManager from './components/AuthManager';
import EmailVerification from './components/EmailVerification';
import './App.css';

type Mode = 'local' | 'ssh';

interface SSHConfig {
  host: string;
  port: number;
  username: string;
  password: string;
  privateKey?: string;
}

function App() {
  const [isVerified, setIsVerified] = useState(false);
  const [mode, setMode] = useState<Mode>('local');
  const [workingDirectory, setWorkingDirectory] = useState<string>('');
  const [sshConfig, setSSHConfig] = useState<SSHConfig>({
    host: '',
    port: 22,
    username: '',
    password: ''
  });
  const [showTerminal, setShowTerminal] = useState(false);
  const [savedConnections, setSavedConnections] = useState<SSHConfig[]>([]);

  const handleLocalStart = () => {
    setMode('local');
    setShowTerminal(true);
  };

  const handleSSHConnect = () => {
    if (sshConfig.host && sshConfig.username) {
      setMode('ssh');
      setShowTerminal(true);
      
      if (!savedConnections.find(c => c.host === sshConfig.host)) {
        setSavedConnections([...savedConnections, { ...sshConfig }]);
      }
    }
  };

  if (!isVerified) {
    return <EmailVerification onVerified={() => setIsVerified(true)} />;
  }

  // Auto-start with custom terminal
  return (
    <div className="min-h-screen bg-gradient-to-br from-omega-darker via-omega-dark to-omega-primary">
      <div className="h-screen flex flex-col">
        <div className="bg-omega-darker border-b border-omega-accent/30 px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-omega-accent to-green-400">
              OMEGA AI
            </h1>
            <span className="text-gray-400">|</span>
            <span className="text-gray-300">Advanced AI Terminal Interface</span>
          </div>
          
          <div className="text-sm text-gray-400">
            SSH: 217.154.51.93
          </div>
        </div>
        
        <div className="flex-1 flex">
          {/* Left side - AI Chat Interface */}
          <div className="w-1/2 border-r border-omega-accent/30">
            <CustomTerminal
              sshHost="217.154.51.93"
              sshUsername="root"
              sshPassword="rVMova3s"
            />
          </div>
          
          {/* Right side - Terminal Console */}
          <div className="w-1/2">
            <Terminal
              mode="ssh"
              sshConfig={{
                host: "217.154.51.93",
                port: 22,
                username: "root",
                password: "rVMova3s"
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );

  /* Original interface - now replaced with auto-terminal
  return (
    <div className="min-h-screen bg-gradient-to-br from-omega-darker via-omega-dark to-omega-primary">
      {!showTerminal ? (
        <div className="flex flex-col items-center justify-center min-h-screen p-8">
          <div className="mb-12 text-center">
            <h1 className="text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-omega-accent to-green-400 mb-4">
              OMEGA AI
            </h1>
            <p className="text-xl text-gray-300">Advanced AI Terminal Interface</p>
            <p className="text-sm text-gray-500 mt-2">Powered by Omega Systems Technology</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 w-full max-w-4xl">
            <div className="bg-omega-dark/50 backdrop-blur-lg rounded-xl p-8 border border-omega-accent/30 hover:border-omega-accent/60 transition-all">
              <h2 className="text-2xl font-bold text-white mb-4">Local Terminal</h2>
              <p className="text-gray-400 mb-6">Connect to your local machine and run Omega AI commands</p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Working Directory (optional)
                  </label>
                  <input
                    type="text"
                    value={workingDirectory}
                    onChange={(e) => setWorkingDirectory(e.target.value)}
                    placeholder="/path/to/directory"
                    className="w-full px-4 py-2 bg-omega-darker border border-omega-accent/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-omega-accent"
                  />
                </div>
                
                <button
                  onClick={handleLocalStart}
                  className="w-full py-3 bg-gradient-to-r from-omega-accent to-blue-500 text-white font-bold rounded-lg hover:opacity-90 transition-opacity"
                >
                  Launch Local Terminal
                </button>
              </div>
            </div>

            <div className="bg-omega-dark/50 backdrop-blur-lg rounded-xl p-8 border border-omega-accent/30 hover:border-omega-accent/60 transition-all">
              <h2 className="text-2xl font-bold text-white mb-4">SSH Connection</h2>
              <p className="text-gray-400 mb-6">Connect to remote Ubuntu servers via SSH</p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Host</label>
                  <input
                    type="text"
                    value={sshConfig.host}
                    onChange={(e) => setSSHConfig({ ...sshConfig, host: e.target.value })}
                    placeholder="example.com or IP address"
                    className="w-full px-4 py-2 bg-omega-darker border border-omega-accent/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-omega-accent"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Username</label>
                    <input
                      type="text"
                      value={sshConfig.username}
                      onChange={(e) => setSSHConfig({ ...sshConfig, username: e.target.value })}
                      placeholder="username"
                      className="w-full px-4 py-2 bg-omega-darker border border-omega-accent/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-omega-accent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Port</label>
                    <input
                      type="number"
                      value={sshConfig.port}
                      onChange={(e) => setSSHConfig({ ...sshConfig, port: parseInt(e.target.value) || 22 })}
                      placeholder="22"
                      className="w-full px-4 py-2 bg-omega-darker border border-omega-accent/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-omega-accent"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
                  <input
                    type="password"
                    value={sshConfig.password}
                    onChange={(e) => setSSHConfig({ ...sshConfig, password: e.target.value })}
                    placeholder="password"
                    className="w-full px-4 py-2 bg-omega-darker border border-omega-accent/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-omega-accent"
                  />
                </div>
                
                <button
                  onClick={handleSSHConnect}
                  disabled={!sshConfig.host || !sshConfig.username}
                  className="w-full py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Connect via SSH
                </button>
              </div>
            </div>
          </div>

          <div className="mt-8 w-full max-w-4xl">
            <AuthManager />
          </div>

          {savedConnections.length > 0 && (
            <div className="mt-8 w-full max-w-4xl">
              <h3 className="text-lg font-semibold text-white mb-4">Recent Connections</h3>
              <div className="flex flex-wrap gap-2">
                {savedConnections.map((conn, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setSSHConfig(conn);
                      setMode('ssh');
                      setShowTerminal(true);
                    }}
                    className="px-4 py-2 bg-omega-dark/50 border border-omega-accent/30 rounded-lg text-gray-300 hover:border-omega-accent hover:text-white transition-all"
                  >
                    {conn.username}@{conn.host}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="h-screen flex flex-col">
          <div className="bg-omega-darker border-b border-omega-accent/30 px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-omega-accent to-green-400">
                OMEGA AI
              </h1>
              <span className="text-gray-400">|</span>
              <span className="text-gray-300">
                {mode === 'local' ? 'Local Terminal' : `SSH: ${sshConfig.host}`}
              </span>
            </div>
            
            <button
              onClick={() => setShowTerminal(false)}
              className="px-4 py-2 bg-omega-dark/50 border border-omega-accent/30 rounded-lg text-gray-300 hover:border-omega-accent hover:text-white transition-all"
            >
              New Connection
            </button>
          </div>
          
          <div className="flex-1 p-4">
            <Terminal
              mode={mode}
              workingDirectory={workingDirectory}
              sshConfig={mode === 'ssh' ? sshConfig : undefined}
            />
          </div>
        </div>
      )}
    </div>
  );
  */
}

export default App;